/**
 * The detailed table's columns, named and ordered like the answer's preview table.
 *
 * The rows come from the offer's own query, so their columns carry the backend's names ("Shipper
 * Standardized Name", "Total Value(USD)"), while the preview was written by the model ("Supplier",
 * "Shipment Value (USD)"). The backend narrows and renames them when the offer was built from the answer
 * (its `table_columns`), but an offer the model prepared itself is made before the table exists. So the
 * match is made here too, against the preview's headers: the first column by position (it is the company),
 * the rest by how alike their words are. A column the preview does not show is kept, after the others.
 */

export type AlignedColumn = { key: string; label: string };

const words = (text: string): Set<string> =>
  new Set(
    text
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      // "shipments" and "shipment" are the same word.
      .map((word) => (word.length > 3 ? word.replace(/s$/, "") : word))
  );

const squash = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]/g, "");

function similarity(a: string, b: string): number {
  if (squash(a) === squash(b)) return 1;
  const left = words(a);
  const right = words(b);
  let shared = 0;
  for (const word of left) if (right.has(word)) shared += 1;
  const union = left.size + right.size - shared;
  return union === 0 ? 0 : shared / union;
}

const isCounter = (header: string) => /^(#|s\.?\s?no\.?|sr\.?\s?no\.?)$/i.test(header.trim());

/** `derived` are the columns the rows carry (serial column excluded); `preview` are the preview table's headers. */
export function alignToPreview(derived: string[], preview: string[]): AlignedColumn[] {
  const wanted = preview.filter((header) => !isCounter(header));
  if (derived.length === 0 || wanted.length === 0) return derived.map((column) => ({ key: column, label: column }));

  const taken = new Set<string>();
  const matched = new Map<string, string>(); // preview header -> derived column

  // The first column is the company, whatever either side calls it.
  matched.set(wanted[0], derived[0]);
  taken.add(derived[0]);

  // The rest, best match first, one derived column per header.
  const pairs: { header: string; column: string; score: number }[] = [];
  for (const header of wanted.slice(1)) for (const column of derived.slice(1)) pairs.push({ header, column, score: similarity(header, column) });
  pairs.sort((x, y) => y.score - x.score);
  for (const { header, column, score } of pairs) {
    if (score < 0.3 || matched.has(header) || taken.has(column)) continue;
    matched.set(header, column);
    taken.add(column);
  }

  const aligned: AlignedColumn[] = wanted.filter((header) => matched.has(header)).map((header) => ({ key: matched.get(header) as string, label: header }));
  for (const column of derived) if (!taken.has(column)) aligned.push({ key: column, label: column });
  return aligned;
}
